import { ReactElement } from 'react';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import {
  hasMemberRole,
  hasTeamFeatures,
  isCompanyAccount,
  isEligibleForCompanyPurchase,
  isEligibleForCompanyTrial,
  isEligibleForTeamPurchase,
  isEligibleForTeamTrial,
  isOrWasCompanyAccount,
  isTrialingCompanyAccount,
  wasTrialingCompanyAccount,
} from '@zapier/design-system-revenue';

import { currentUser as CurrentUserQuery } from './__generated__/currentUser';

/* eslint-disable graphql/no-deprecated-fields */
const currentUserQuery = gql`
  query currentUser {
    currentUserV2 {
      email
      fullName
      id
      imageUrl
      isDeveloper
      isStaff
    }

    isAuthenticatedV2

    accounts {
      companyAccountEndDate
      currentUserRole
      hasBeenInCompaniesTrial
      companiesTrialEndDate
      id
      isTrialing
      isTrialingCompanies
      plan {
        allowCompanies
        hasTeams
        name
        id
      }
      teamAccountEndDate
    }
  }
`;
/* eslint-enable graphql/no-deprecated-fields */

const useCurrentUser = () => {
  const { data, loading } = useQuery<CurrentUserQuery>(currentUserQuery, {
    ssr: false,
  });

  const { currentUserV2, isAuthenticatedV2, accounts } = data || {};
  const { email, fullName, id, imageUrl, isDeveloper, isStaff } =
    currentUserV2 || {};

  const accountId = (id && parseInt(id, 10)) || null;
  const isLoggedIn = Boolean(isAuthenticatedV2);

  const companyTrialAccount =
    accounts && accounts.find(account => !!account.companiesTrialEndDate);

  const companiesTrialEndDate =
    companyTrialAccount && companyTrialAccount.companiesTrialEndDate;

  const currentUser = {
    accountId,
    email,
    fullName,
    hasBeenInCompaniesTrial: wasTrialingCompanyAccount(accounts),
    hasMemberRole: hasMemberRole(accounts),
    hasTeamAccount: hasTeamFeatures(accounts),
    imageUrl,
    isCompaniesAccount: isCompanyAccount(accounts),
    isDeveloper: Boolean(isDeveloper),
    isEligibleForCompanyPurchase: isEligibleForCompanyPurchase(accounts),
    isEligibleForCompanyTrial: isEligibleForCompanyTrial(accounts),
    isEligibleForTeamPurchase: isEligibleForTeamPurchase(accounts),
    isEligibleForTeamTrial: isEligibleForTeamTrial(accounts),
    isLoggedIn,
    isOrWasCompaniesAccount: isOrWasCompanyAccount(accounts),
    isStaff: Boolean(isStaff),
    isTrialingCompanies: isTrialingCompanyAccount(accounts),
    companiesTrialEndDate,
  };

  return {
    currentUser,
    isAuthenticated: isLoggedIn,
    isLoading: loading,
  };
};

/**
 * This is exposed for _app.js and other legacy uses you'll most likely want to
 * leverage the hook that is located above.
 */
export const WithCurrentUser = (props: {
  children: (result: ReturnType<typeof useCurrentUser>) => ReactElement;
}) => {
  const currentUserResult = useCurrentUser();

  return props.children(currentUserResult);
};

export default useCurrentUser;
